import { useEffect, useState } from 'react';
import { Card, Stat, Badge, Button } from '../../components/ui';
import { money, pct } from '../../lib/format';
import { SEGMENT_BY_ID, TECH_BY_ID } from '../../lib/constants';
import { useDecisions, pendingCount } from '../../store/decisions';
import { useMyJournals, useSubmitJournal } from '../../hooks/queries';
import { tutSignal } from '../../tutorial/useTutorial';
import type { ScreenProps } from './types';

/* Per-quarter strategy journal (Phase 3 #2). The entry is keyed to the current
 * quarter; re-saving revises it. Instructors read these in the review drilldown
 * and the journal component feeds the class grade. Module-level component —
 * stable identity across Overview renders (see HANDOFF porting pattern #6). */
function JournalCard({ classId, round }: { classId?: string; round: number }) {
  const journalsQ = useMyJournals(classId);
  const submit = useSubmitJournal(classId);
  const existing = journalsQ.data?.find((j) => j.round === round);
  const [text, setText] = useState('');
  const [loadedFor, setLoadedFor] = useState<number | null>(null);

  // Prefill once per round from the saved entry (don't clobber in-progress edits).
  useEffect(() => {
    if (journalsQ.data && loadedFor !== round) {
      setText(existing?.text ?? '');
      setLoadedFor(round);
    }
  }, [journalsQ.data, existing, round, loadedFor]);

  if (!classId) return null;
  const past = (journalsQ.data ?? []).filter((j) => j.round !== round);

  return (
    <Card tut="journal-card">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-mut">Strategy journal — Q{round}</h3>
        {existing && <Badge tone="good">Saved</Badge>}
      </div>
      <p className="mb-2 text-xs text-dim">
        What is your plan this quarter, and why? Your instructor reads these alongside your decisions; journals count toward your grade.
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        maxLength={10_000}
        placeholder="e.g. Holding price on Lumina to defend share while the Q3 marketing push builds awareness in Europe…"
        className="w-full rounded-lg border border-line bg-[color:var(--in-bg)] p-2.5 text-sm text-txt placeholder:text-dim focus:border-acc/60 focus:outline-none"
      />
      <div className="mt-2 flex items-center gap-3">
        <Button
          onClick={() => submit.mutate({ round, text: text.trim() }, { onSuccess: () => tutSignal('journal-saved') })}
          disabled={!text.trim() || submit.isPending}
        >
          {submit.isPending ? 'Saving…' : existing ? 'Update entry' : 'Save entry'}
        </Button>
        {submit.isError && <span className="text-xs text-bad">Could not save — are you enrolled in this class?</span>}
        {submit.isSuccess && !submit.isPending && <span className="text-xs text-good">Saved.</span>}
      </div>
      {past.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-mut hover:text-txt">Past entries ({past.length})</summary>
          <ul className="mt-2 space-y-2">
            {past.map((j) => (
              <li key={j.id} className="rounded-lg border border-[color:var(--well-b)] bg-[color:var(--well)] p-2 text-xs">
                <span className="font-semibold text-mut">Q{j.round ?? '—'}</span>
                <p className="mt-0.5 whitespace-pre-wrap text-dim">{j.text}</p>
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

export function Overview({ detail }: ScreenProps) {
  const s = detail.state as any;
  const draft = useDecisions((z) => z.draft);
  const lastComposite = detail.snapshots.at(-1)?.composite ?? null;
  const products = (s.products ?? []) as any[];
  const markets = s.markets ?? {};
  const activeMarkets = Object.values(markets).filter((m: any) => m?.sub || m?.dist || m?.carrier).length;
  const news = (s.news ?? []) as any[];
  const activeTech = s.rnd?.active ? TECH_BY_ID[s.rnd.active] : null;
  // Overdraft / insolvency banner (DESIGN-insolvency-and-direct-fulfilment §6.1)
  const lastH = (s.history ?? []).at(-1) ?? {};
  const loan: number = s.loan ?? 0;
  const odRate = ((s.revolverPenalty ?? 0.035) * 100).toFixed(1);
  const odPts: number = lastH.odPenalty ?? 0;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat tut="stat-cash" label="Cash" value={money(s.cash)} accent={s.cash < 0 ? 'text-bad' : 'text-good'} />
        <Stat label="Brand" value={Math.round(s.brand ?? 0)} accent="text-vio" />
        <Stat tut="stat-composite" label="Composite" value={lastComposite != null ? Math.round(lastComposite) : '—'} accent="text-acc" />
        <Stat label="Quarter" value={`${s.round} / ${s.maxRounds}`} />
      </div>

      {s.insolvent ? (
        <Card className="border-bad/60 bg-bad/10 text-sm">
          <div className="font-semibold">⚠️ Insolvency watch — cumulative losses have used up your book equity.</div>
          <p className="mt-1 text-mut">
            Banks won't lend, bonds are closed, and dividends and buybacks are suspended until equity is positive again.
            You can still raise equity, cut costs, or trade your way back. Overdraft outstanding: <b className="text-txt">{money(loan)}</b> at {odRate}%/qtr.
            See the Financing screen.
          </p>
        </Card>
      ) : loan > 0 ? (
        <Card className="border-warn/60 bg-warn/10 text-sm">
          <div className="font-semibold">🚨 You are running on the emergency overdraft: {money(loan)} at {odRate}%/qtr.</div>
          <p className="mt-1 text-mut">
            It cost you {odPts} finance points last quarter and weighs on your share price. Refinance with a term loan or raise equity on the Financing screen.
          </p>
        </Card>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-mut">Portfolio</h3>
            <Badge tone="acc">{products.length} product{products.length === 1 ? '' : 's'}{(draft.newProducts?.length ?? 0) > 0 ? ` · ${draft.newProducts!.length} staged` : ''}</Badge>
          </div>
          {products.length === 0 ? (
            <p className="text-sm text-mut">No products yet — design one on the Products screen.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {products.map((p) => (
                <li key={p.id} className="flex items-center justify-between">
                  <span>{p.name} <span className="text-dim">· {SEGMENT_BY_ID[p.targetSeg]?.name ?? p.targetSeg}</span></span>
                  <span className="text-mut">{(p.gens ?? []).length} gen</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex gap-4 text-xs text-mut">
            <span>Markets active: <b className="text-txt">{activeMarkets}</b></span>
            <span>R&amp;D: <b className="text-txt">{activeTech ? activeTech.name : 'idle'}</b></span>
            <span>Techs done: <b className="text-txt">{s.rnd?.done?.length ?? 0}</b></span>
          </div>
        </Card>

        <Card>
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-mut">Latest news</h3>
          {news.length === 0 ? (
            <p className="text-sm text-mut">No news yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {news.slice(0, 5).map((n, i) => (
                <li key={i} className="flex gap-2">
                  <span>{n.icon ?? '•'}</span>
                  <span className="text-mut">{n.text}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card>
        <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-mut">This quarter</h3>
        <p className="text-sm text-mut">
          You have <b className="text-txt">{pendingCount(draft)}</b> staged change{pendingCount(draft) === 1 ? '' : 's'}.
          Set decisions across the tabs, then submit the quarter to resolve the market against the AI rivals.
        </p>
        {detail.snapshots.length > 0 && (
          <p className="mt-1 text-xs text-dim">
            Last quarter revenue {money(detail.snapshots.at(-1)?.revenue)} · profit {money(detail.snapshots.at(-1)?.profit)} · share {pct(detail.snapshots.at(-1)?.share)}
          </p>
        )}
      </Card>

      <JournalCard classId={detail.game.classId} round={s.round as number} />
    </div>
  );
}
